import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  ViewChild,
  input,
  model,
  Signal,
  effect,
} from '@angular/core';
import { combineLatest, from, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { StatusBar, Style } from '@capacitor/status-bar';

import { ProductKeys } from '@config/keys/product.keys';
import { AdditionGroupKeys } from '@config/keys/shop.keys';

import { ProductTypeValues } from '@config/values/product.values';
import { NativeService } from '@core/services/native/native.service';

import { IAdditionGroup } from '@core/models/shop.model';
import { DoodDeclinableModel, DoodProductModel, IProductLabel } from '@core/models/product.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { CartUtilService } from '@shared/local-store/cart/util/cart-util.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AutoFillCartItemRulesAdditionsGroup } from '@shared/local-store/cart/auto-fill-cart-item-rules/auto-fill-cart-item-rules-additions-group';
import { forIn } from 'lodash';
import { DeclinableUtils } from '@app/utilities/declinable.util';
import { ProductService } from '@core/services/product/product.service';
import { ProductPanelAdditionGroupUtil } from './product-panel-addition-group.util';

@Component({
  selector: 'app-product-panel-atom',
  templateUrl: './product-panel-atom.component.html',
  styleUrls: ['./product-panel-atom.component.scss'],
  standalone: false,
  host: {
    'data-testid': 'product-details-container',
  },
})
export class ProductPanelAtomComponent {
  private readonly productService = inject(ProductService);

  readonly product = input.required<DoodProductModel>();
  readonly additions = input.required<DoodProductModel[]>();
  readonly additionsByGroup = input.required<IAdditionGroup[]>();
  readonly compoundItems = input<DoodDeclinableModel[]>([]);
  readonly quantity = model(1);
  readonly isDisableButton = input(true);
  readonly isEdit = input(false);
  readonly shopOpensAt = input<string>();
  readonly allowAddToCart = input(true);
  readonly index = input.required<number>();
  readonly displayPrice = input<boolean | undefined>(true);
  readonly maxProduct = input<number>();
  readonly headingColor = input<IContentBuilderFieldColor>({
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  });
  readonly descriptionColor = input<IContentBuilderFieldColor>({
    value: 'neutral-700',
    type: ColorFieldTypesValues.Palette,
  });
  readonly inactiveStateColor = input<IContentBuilderFieldColor>({
    value: 'neutral-500',
    type: ColorFieldTypesValues.Palette,
  });
  readonly priceColor = input<IContentBuilderFieldColor>({
    value: 'neutral-700',
    type: ColorFieldTypesValues.Palette,
  });
  readonly tagColors = input.required<ContentBuilderFieldColors>();

  readonly backgroundColor = input<IContentBuilderFieldColor | undefined>({
    value: '#FFFFFF',
    type: ColorFieldTypesValues.Hex,
  });

  readonly textColor = input<IContentBuilderFieldColor>();

  readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();

  readonly buttonTextColor = input<IContentBuilderFieldColor>();

  readonly hideAdditionGroupDetails = input(false);

  readonly orderValidationInProgress = input(false);

  @Output() closePanel = new EventEmitter<Event>();
  @Output() changeQuantity = new EventEmitter<number>();
  @Output() changeAdditionQuantity = new EventEmitter<{ quantity: number; id: string }>();
  @Output() toggleAddition = new EventEmitter<DoodProductModel>();
  @Output() addProduct = new EventEmitter<Event | undefined>();
  @Output() removeProduct = new EventEmitter<string>();
  @Output() openCompound = new EventEmitter<string>();

  @ViewChild('declinableOptionsElement')
  declinableOptionsElement?: ElementRef;

  additionGroupKeys = AdditionGroupKeys;

  productTypeValues = ProductTypeValues;

  isButtonDisable = computed(() => {
    const product = this.product();
    if (!product?.[ProductKeys.Id] || this.shopOpensAt()) {
      return true;
    }
    if (this.isDisableButton()) {
      return true;
    }
    if (
      product[ProductKeys.Type] === ProductTypeValues.Declinable &&
      !DeclinableUtils.areAllDeclinableCategoriesValid(this.compoundItems())
    ) {
      return true;
    }
    return false;
  });

  isNotched = toSignal(from(NativeService.isNotched()), {
    initialValue: false,
  });

  orderedAdditionGroups: Signal<IAdditionGroup[]> = computed(() => {
    const additionsByGroup = this.additionsByGroup();
    return additionsByGroup
      ? [...CartUtilService.mapAdditionGroupsWithPosition({
          additionsGroups: additionsByGroup,
          simpleProductAdditions: this.product().additions?.products ?? [],
        })].sort((a, b) => {
          return (a?.[AdditionGroupKeys.Position] ?? 0) - (b?.[AdditionGroupKeys.Position] ?? 0);
        })
      : [];
  });

  productKeys = ProductKeys;

  protected readonly allergensLine = toSignal(
    toObservable(this.product).pipe(
      switchMap(product => {
        const allergens = product?.[ProductKeys.Allergens] ?? [];
        if (!allergens.length) {
          return of('');
        }

        const nameKeys = allergens.map(allergen => `allergens.${allergen.name}`);

        return this.translateService.stream(nameKeys).pipe(
          switchMap(nameTranslations => {
            const list = nameKeys
              .map(key => `${nameTranslations[key] ?? ''}`.trim())
              .filter(name => name.length > 0)
              .join(', ');

            if (!list) {
              return of('');
            }

            return this.translateService.stream('product-add-modal.allergens-line', {
              allergens: list,
            });
          }),
        );
      }),
    ),
    { initialValue: '' },
  );

  private readonly filterColors$ =
    this.marketplaceSelector.selectMarketPlaceProductLabelsFilterColors;

  protected readonly vm$ = combineLatest({
    filterColors: this.filterColors$,
  });

  protected readonly hasAdditionGroupsThatNeedsUserChoice = computed(() => {
    const product = this.product();
    if (!product) {
      return false;
    }
    if (
      product.type === ProductTypeValues.Declinable ||
      product.type === ProductTypeValues.Addition
    ) {
      return true;
    }
    // simple product that may have additions
    const hasAdditions = product.additions.products.length > 0;
    if (!hasAdditions) {
      return true;
    }
    const additionsByGroups = this.additionsByGroup();

    if (!additionsByGroups) {
      return false;
    }
    // check if additions groups are auto selected
    const hasAdditionGroupsThatNeedsUserChoice = additionsByGroups.some(group => {
      const isOptionalWithoutMaxCountDefined = !group.min_count && !group.max_count;
      if (isOptionalWithoutMaxCountDefined) {
        return true;
      }
      return group.min_count !== group.max_count || group.items.length > 1;
    });
    return hasAdditionGroupsThatNeedsUserChoice;
  });

  protected readonly canDisplayProductPanel = this.hasAdditionGroupsThatNeedsUserChoice;

  private readonly _autoAddAdditionsEffect = effect(() => {
    if (this.isEdit()) {
      this._autoAddAdditionsEffect.destroy();
      return;
    }

    const product = this.product();
    if (!product) {
      return false;
    }
    if (
      product.type === ProductTypeValues.Declinable ||
      product.type === ProductTypeValues.Addition
    ) {
      this._autoAddAdditionsEffect.destroy();
      return true;
    }
    // simple product that may have additions
    const hasAdditions = product.additions.products.length > 0;
    if (!hasAdditions) {
      this._autoAddAdditionsEffect.destroy();
      return true;
    }
    const additionsByGroups = this.additionsByGroup();

    if (!additionsByGroups) {
      this._autoAddAdditionsEffect.destroy();
      return false;
    }
    // check if additions groups are auto selected
    const autoFilledAdditionsIds =
      AutoFillCartItemRulesAdditionsGroup.getAutoFilledAdditionIds(additionsByGroups);
    const autoFilledAdditions = autoFilledAdditionsIds.reduce(
      (acc, id) => {
        const existing = acc[id];
        if (existing) {
          existing.quantity += 1;
          return acc;
        }

        const found = additionsByGroups.reduce<DoodProductModel | undefined>((acc, group) => {
          if (acc) return acc;
          return group[AdditionGroupKeys.Items].find(
            (it: DoodProductModel) => it[ProductKeys.Id] === id,
          );
        }, undefined);

        acc[id] = { addition: found, quantity: 1, additionId: id };
        return acc;
      },
      {} as Record<
        string,
        { addition: DoodProductModel | undefined; quantity: number; additionId: string }
      >,
    );
    forIn(autoFilledAdditions, ({ addition, quantity, additionId }) => {
      if (addition) {
        addition[ProductKeys.IsChecked] = true;
        addition[ProductKeys.Quantity] = quantity;
        this.toggleAddition.emit(addition);
        this.changeAdditionQuantity.emit({ quantity, id: additionId });
      }
    });
    this._autoAddAdditionsEffect.destroy();
  });

  constructor(
    private readonly translateService: TranslateService,
    private marketplaceSelector: MarketplaceStoreSelector,
  ) {
    effect(async () => {
      const isNotched = this.isNotched();
      if (isNotched) {
        await StatusBar.setStyle({ style: Style.Dark });
      }
    });
  }

  close(event: Event): void {
    this.closePanel.emit(event);
  }

  addToCart(event: Event): void {
    this.addProduct.emit(event);
  }

  scrollToOptions(): void {
    this.declinableOptionsElement?.nativeElement.scrollIntoView({
      behavior: 'smooth',
    });
  }

  isAvailable(product: DoodProductModel): boolean {
    return this.productService.isAvailable(product);
  }

  selectAddition(addition: DoodProductModel): void {
    if (!this.isAvailable(addition)) {
      addition[ProductKeys.IsChecked] = false;
      addition[ProductKeys.Quantity] = 0;
      return;
    }
    if (addition[ProductKeys.IsChecked]) {
      addition[ProductKeys.Quantity] = 1;
    } else {
      addition[ProductKeys.Quantity] = 0;
    }
    this.toggleAddition.emit(addition);
  }

  changeNbProduct(quantity: number): void {
    this.quantity.set(quantity);
    this.changeQuantity.emit(quantity);
  }

  changeNbAddition(quantity: number, id: string, addition: DoodProductModel): void {
    addition[ProductKeys.Quantity] = quantity;
    this.changeAdditionQuantity.emit({ quantity, id });
  }

  visibleLabels(labels: IProductLabel[] | undefined): IProductLabel[] {
    if (!labels) {
      return [];
    }

    // Shown on the product card (`visible`) or only in this product modal (`visible_in_detail`)
    const sortedLabels = labels
      .filter(label => label.visible || label.visible_in_detail)
      .sort((a, b) => a.position - b.position);

    // Suppression des doublons basés sur `name`
    const uniqueLabelsMap = new Map<string, IProductLabel>();
    sortedLabels.forEach(label => {
      if (!uniqueLabelsMap.has(label.name)) {
        uniqueLabelsMap.set(label.name, label);
      }
    });

    return Array.from(uniqueLabelsMap.values());
  }

  isDeclinableCategoryValid(item: DoodDeclinableModel): boolean {
    return DeclinableUtils.isDeclinableCategoryValid(item);
  }

  isAdditionGroupMinCountReached(additionGroup: IAdditionGroup): boolean {
    return ProductPanelAdditionGroupUtil.isMinCountReached(additionGroup);
  }

  additionGroupTitle = (additionGroup: IAdditionGroup): string | undefined => {
    let chooseMessage = '';

    const translations: Record<string, any> = this.translateService.instant([
      'product-add-modal.choose',
      'product-add-modal.choose-at-least',
      'product-add-modal.choose-between',
      'product-add-modal.and',
    ]);
    if (!additionGroup.max_count && additionGroup.min_count === 0) {
      chooseMessage = '';
    } else if (!additionGroup.max_count && additionGroup.min_count > 0) {
      chooseMessage = `${translations['product-add-modal.choose-at-least']} ${additionGroup.min_count}`;
    } else if (additionGroup.max_count === additionGroup.min_count) {
      chooseMessage = `${translations['product-add-modal.choose']} ${additionGroup.min_count}`;
    } else if (additionGroup.max_count && additionGroup.min_count) {
      chooseMessage = `${translations['product-add-modal.choose-between']} ${additionGroup.min_count} ${translations['product-add-modal.and']} ${additionGroup.max_count}.`;
    } else if (additionGroup.max_count && additionGroup.min_count === 0) {
      chooseMessage = `${translations['product-add-modal.choose']} ${additionGroup.max_count} maximum.`;
    } else {
      chooseMessage = `${additionGroup.min_count} - ${additionGroup.max_count}.`;
    }

    return chooseMessage;
  };

  getMaxAdditionCount(additionGroup: IAdditionGroup, addition: DoodProductModel): number {
    const otherAdditionsQuantity = additionGroup[AdditionGroupKeys.Items].reduce((_total, item) => {
      if (item === addition) {
        return _total;
      }
      return _total + ((item[ProductKeys.IsChecked] && (item[ProductKeys.Quantity] || 1)) || 0);
    }, 0);

    let groupMaxCount;

    if (additionGroup[AdditionGroupKeys.MaxCount]) {
      groupMaxCount = Math.max(
        Math.min(
          (additionGroup[AdditionGroupKeys.MaxCount] || Infinity) - otherAdditionsQuantity,
          addition[ProductKeys.MaxCount] || Infinity,
        ),
        0,
      );
    } else {
      groupMaxCount = Math.max(Math.min(addition[ProductKeys.MaxCount] || Infinity), 0);
    }

    if (addition[ProductKeys.InventoryTracked]) {
      groupMaxCount = Math.min(groupMaxCount, addition[ProductKeys.InventoryAvailable] || 0);
    }

    return groupMaxCount;
  }
}
